import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ENGLISH } from "@/lib/betaReport/i18n";

// Admin → Beta Reports (Beta Tester standards, 2026-10-30): every submitted Beta Report,
// filterable (the brief, §4), with a CSV export of sanitised metadata and answers, and
// how many opened forms were never submitted.

const FILTERS = [
  ["mode", "Mode", ["", "guided", "live"]],
  ["language", "Language", ["", "en", "pt-BR", "es-419", "fr-CA"]],
  ["device", "Device", ["", "desktop", "mobile", "tablet"]],
  ["rating", "Overall rating", ["", "1", "2", "3", "4", "5"]],
  ["problems", "Reported problems", ["", "yes"]],
];

export default function AdminBetaReports() {
  const [filters, setFilters] = useState({});
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const load = useCallback(() => {
    api.get("/admin/beta-reports", { params }).then(({ data: d }) => setData(d)).catch(() => setData({ reports: [] }));
  }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    const r = await api.get("/admin/beta-reports.csv", { params, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "beta-reports.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)] p-6" data-testid="admin-beta-reports">
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Beta Reports</h1>
          <div className="flex items-center gap-4">
            <Link to="/admin/beta-report-wording" className="text-sm text-[color:var(--brand)]" data-testid="beta-reports-wording-link">Edit the form's wording →</Link>
            <Link to="/admin" className="text-sm text-[color:var(--brand)]">← Admin</Link>
          </div>
        </div>
        {data?.completion && (
          <p className="text-sm text-[color:var(--text-secondary)]" data-testid="beta-report-completion">
            Forms opened: {data.completion.opened} · submitted: {data.completion.submitted} · not submitted: {data.completion.abandoned}
          </p>
        )}
        <div className="flex flex-wrap items-end gap-3">
          {FILTERS.map(([key, label, values]) => (
            <label key={key} className="text-xs">
              <span className="block text-[color:var(--text-secondary)] mb-1">{label}</span>
              <select value={filters[key] || ""} onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))}
                className="rounded-lg border border-[color:var(--border-stone)] bg-white px-2 py-1.5 text-sm">
                {values.map((v) => <option key={v} value={v}>{v || "All"}</option>)}
              </select>
            </label>
          ))}
          <button type="button" onClick={downloadCsv} className="ml-auto rounded-full bg-[color:var(--brand)] text-white px-4 py-2 text-sm" data-testid="beta-reports-csv">Download CSV</button>
        </div>
        <table className="w-full text-sm bg-white rounded-2xl border border-[color:var(--border-stone)]">
          <thead className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)]">
            <tr>
              <th className="text-left px-3 py-2">Submitted</th>
              <th className="text-left px-3 py-2">Tester</th>
              <th className="text-left px-3 py-2">Mode</th>
              <th className="text-left px-3 py-2">Language</th>
              <th className="text-left px-3 py-2">Overall</th>
              <th className="text-left px-3 py-2">Problems</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {(data?.reports || []).map((r) => (
              <React.Fragment key={r.report_id}>
                <tr className="border-t border-[color:var(--border-stone)]">
                  <td className="px-3 py-2">{new Date(r.submitted_at).toLocaleString()}</td>
                  <td className="px-3 py-2">{r.first_name || r.user_id}</td>
                  <td className="px-3 py-2">{ENGLISH.ui.modes[r.metadata?.interview_mode] || r.metadata?.interview_mode}</td>
                  <td className="px-3 py-2">{r.metadata?.interface_language}</td>
                  <td className="px-3 py-2">{r.answers?.a_overall ?? "—"}</td>
                  <td className="px-3 py-2">{r.answers?.b_worked === "no" ? (r.answers?.b_problem_types || []).join(", ") : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" onClick={() => setOpen(open === r.report_id ? null : r.report_id)} className="text-[color:var(--brand)]">
                      {open === r.report_id ? "Hide" : "View"}
                    </button>
                  </td>
                </tr>
                {open === r.report_id && (
                  <tr><td colSpan={7} className="px-3 py-3 bg-[color:var(--bg-surface-hover)]"><ReportDetail r={r} /></td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-[color:var(--text-secondary)]">Reports from the earlier feedback form are in <Link to="/admin/beta-feedback" className="underline">Beta Feedback</Link>.</p>
      </div>
    </div>
  );
}

function ReportDetail({ r }) {
  const label = (v) => (Array.isArray(v) ? v.map((x) => ENGLISH.opt[x] || x).join(", ") : ENGLISH.opt[v] || ENGLISH.ratings[v] || String(v));
  const meta = r.metadata || {};
  return (
    <div className="space-y-3 text-sm">
      <div className="text-xs text-[color:var(--text-secondary)]">
        Session {r.session_id} · {meta.practice_format} · level {meta.interview_level} · {meta.target_job_title || "—"} ·
        {" "}{meta.browser} / {meta.os} / {meta.device_type} ({meta.viewport}) · Partner Code {meta.partner_code || "—"} ·
        {" "}errors logged: {(meta.client_errors || []).length}
        {meta.answer_script_used && <> · <strong>answers read from the answer script</strong></>}
      </div>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
        {Object.entries(r.answers || {}).map(([id, v]) => (
          <div key={id}>
            <dt className="text-xs text-[color:var(--text-secondary)]">{ENGLISH.q[id] || id}</dt>
            <dd className="whitespace-pre-wrap">{label(v)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
